/**
 * Custom hook to manage the logic for publishing a report template, including file uploads to S3 and GraphQL mutations.
 *
 * The hook handles both creating new templates and updating existing ones, with support for system templates that require auto-approval.
 * It also manages the upload process, including converting data URIs to Blobs, uploading directly to S3 using pre-signed URLs, and completing the upload with the backend.
 * Error handling is included to provide user feedback via toast notifications.
 */
import { useCallback, useState } from "react";
import { toast } from "@jsq/ds/core";
import {
  useApproveReportTemplateVersionMutation,
  useCompleteReportTemplateUploadMutation,
  useCreateReportTemplateWithUploadMutation,
  useUpdateReportTemplateMutation,
  useUploadReportTemplateVersionMutation,
} from "~/graphql/supergraphHooks";
import { getErrorMessage } from "~/utils/errors";
import { requireReportTypeEnum } from "../types";
import { dataURItoBlob, getContentTypeFromExtension } from "../utils";
import type { TemplateFileData } from "../components/TemplateCreation";

const MAX_FILE_SIZE_MB = 50;
const UPLOAD_TIMEOUT_MS = 5 * 60 * 1000;

export type UploadResult = { templateId: number; versionId: number };

type GeneralSettings = {
  templateName: string;
  templateType: string;
  dataPackage: string;
  query: string;
};

type DataPackageOption = { id: number; name: string; isSystem: boolean };

type UpdateSettings = { dataPackage: string; query: string };

type UsePublishTemplateParams = {
  isNewTemplate: boolean;
  isUpdateMode: boolean;
  isSystemTemplate: boolean;
  templateIdNum: number | null;
  templateName: string | null;
  arenaIdNum: number | null;
  generalSettings: GeneralSettings;
  originalSettings: GeneralSettings | null;
  dataPackageName: string;
  dataPackageOptions: DataPackageOption[];
  updateSettings: UpdateSettings;
  pendingTemplateFile: TemplateFileData | null;
  onPublishSuccess: () => void;
};

export const usePublishTemplate = ({
  isNewTemplate,
  isUpdateMode,
  isSystemTemplate,
  templateIdNum,
  templateName,
  arenaIdNum,
  generalSettings,
  originalSettings,
  dataPackageName,
  dataPackageOptions,
  updateSettings,
  pendingTemplateFile,
  onPublishSuccess,
}: UsePublishTemplateParams) => {
  const [isUploading, setIsUploading] = useState(false);

  const [createTemplateWithUpload, { loading: createLoading }] =
    useCreateReportTemplateWithUploadMutation();
  const [uploadVersion, { loading: uploadVersionLoading }] =
    useUploadReportTemplateVersionMutation();
  const [completeUpload, { loading: completeLoading }] =
    useCompleteReportTemplateUploadMutation();
  const [updateTemplate, { loading: updateLoading }] = useUpdateReportTemplateMutation();
  const [approveVersion] = useApproveReportTemplateVersionMutation();

  const isSaving =
    createLoading || uploadVersionLoading || updateLoading || completeLoading || isUploading;

  /**
   * Uploads template file directly to S3 using a pre-signed URL.
   * Returns the ETag (checksum) from S3 which is needed to complete the upload.
   *
   * The upload flow is a 3-step process to ensure data integrity:
   * 1. createTemplateWithUpload - Backend creates DB record and returns pre-signed S3 URL
   * 2. uploadTemplateFile - Frontend uploads directly to S3 (this function)
   * 3. completeUpload - Backend verifies the upload via ETag and marks it complete
   */
  const uploadTemplateFile = useCallback(
    async (uploadUrl: string, blob: Blob, contentType: string): Promise<string> => {
      setIsUploading(true);
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), UPLOAD_TIMEOUT_MS);

      try {
        const uploadResponse = await fetch(uploadUrl, {
          method: "PUT",
          body: blob,
          signal: controller.signal,
          headers: { "Content-Type": contentType },
        });
        clearTimeout(timeoutId);

        if (!uploadResponse.ok) {
          const errorText = await uploadResponse.text().catch(() => "");
          throw new Error(
            `Failed to upload template file (${uploadResponse.status})${errorText ? `: ${errorText}` : ""}`,
          );
        }

        const etag = uploadResponse.headers.get("ETag");
        if (!etag) {
          throw new Error("Upload response missing ETag header");
        }
        return etag.replace(/"/g, "");
      } catch (error) {
        clearTimeout(timeoutId);
        if (error instanceof Error && error.name === "AbortError") {
          throw new Error("Upload timed out. Please try again with a smaller file.");
        }
        throw error;
      } finally {
        setIsUploading(false);
      }
    },
    [],
  );

  const performVersionUpload = useCallback(
    async (templateFile: TemplateFileData, requestApproval: boolean): Promise<UploadResult> => {
      if (!templateIdNum) throw new Error("Template ID is required for version upload");

      let blob: Blob;
      try {
        blob = dataURItoBlob(templateFile.dataURI);
      } catch (error) {
        throw new Error(
          `Invalid template file: ${error instanceof Error ? error.message : "Unknown error"}`,
        );
      }

      if (blob.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        throw new Error(`File size exceeds ${MAX_FILE_SIZE_MB}MB limit`);
      }

      const contentType = getContentTypeFromExtension(templateFile.fileType);
      const fileName = `${templateName ?? "template"}.${templateFile.fileType}`;
      const dpName =
        dataPackageOptions.find((p) => String(p.id) === updateSettings.dataPackage)?.name ?? "";
      const queryId = Number(updateSettings.query);
      if (!queryId) throw new Error("Please select a query before publishing");

      const versionResult = await uploadVersion({
        variables: {
          input: {
            templateId: templateIdNum,
            fileName,
            contentType,
            fileSize: blob.size,
            dataPackage: { id: Number(updateSettings.dataPackage), name: dpName },
            queryId,
          },
        },
      });

      const uploadData = versionResult.data?.uploadReportTemplateVersion;
      if (!uploadData) throw new Error("Failed to initiate version upload");

      const checksum = await uploadTemplateFile(uploadData.uploadUrl, blob, contentType);

      await completeUpload({
        variables: { versionId: uploadData.versionId, checksum, requestApproval },
      });

      return { templateId: templateIdNum, versionId: uploadData.versionId };
    },
    [
      templateIdNum,
      templateName,
      updateSettings.dataPackage,
      updateSettings.query,
      dataPackageOptions,
      uploadVersion,
      uploadTemplateFile,
      completeUpload,
    ],
  );

  const performTemplateUpload = useCallback(
    async (templateFile: TemplateFileData, requestApproval: boolean): Promise<UploadResult> => {
      let blob: Blob;
      try {
        blob = dataURItoBlob(templateFile.dataURI);
      } catch (error) {
        throw new Error(
          `Invalid template file: ${error instanceof Error ? error.message : "Unknown error"}`,
        );
      }

      if (blob.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        throw new Error(`File size exceeds ${MAX_FILE_SIZE_MB}MB limit`);
      }

      const contentType = getContentTypeFromExtension(templateFile.fileType);
      const fileName = `${generalSettings.templateName}.${templateFile.fileType}`;
      const reportType = requireReportTypeEnum(generalSettings.templateType);

      const queryId = Number(generalSettings.query);
      if (!queryId) throw new Error("Please select a query before publishing");

      const createResult = await createTemplateWithUpload({
        variables: {
          input: {
            name: generalSettings.templateName,
            arenaId: arenaIdNum,
            reportType,
            dataPackage: {
              id: Number(generalSettings.dataPackage),
              name: dataPackageName,
            },
            queryId,
            fileName,
            contentType,
            fileSize: blob.size,
          },
        },
      });

      const uploadData = createResult.data?.createReportTemplateWithUpload;
      if (!uploadData) {
        throw new Error("Failed to initiate template upload");
      }

      const checksum = await uploadTemplateFile(uploadData.uploadUrl, blob, contentType);

      await completeUpload({
        variables: {
          versionId: uploadData.versionId,
          checksum,
          requestApproval,
        },
      });

      return {
        templateId: uploadData.template.id,
        versionId: uploadData.versionId,
      };
    },
    [
      generalSettings.templateName,
      generalSettings.templateType,
      generalSettings.dataPackage,
      generalSettings.query,
      dataPackageName,
      arenaIdNum,
      createTemplateWithUpload,
      uploadTemplateFile,
      completeUpload,
    ],
  );

  const handlePublish = useCallback(
    async (shouldAutoApprove = false) => {
      try {
        let uploadResult: UploadResult | undefined;

        if (isUpdateMode) {
          if (!pendingTemplateFile) {
            toast.error("Please design a template before publishing");
            return;
          }

          uploadResult = await performVersionUpload(
            pendingTemplateFile,
            !shouldAutoApprove && !isSystemTemplate,
          );
        } else if (isNewTemplate) {
          if (!pendingTemplateFile) {
            toast.error("Please design a template before publishing");
            return;
          }

          uploadResult = await performTemplateUpload(
            pendingTemplateFile,
            !shouldAutoApprove && !isSystemTemplate,
          );
        } else if (templateIdNum) {
          const reportTypeUpdate =
            originalSettings &&
            generalSettings.templateType !== originalSettings.templateType &&
            generalSettings.templateType !== ""
              ? requireReportTypeEnum(generalSettings.templateType)
              : undefined;
          await updateTemplate({
            variables: {
              id: templateIdNum,
              input: {
                name: generalSettings.templateName,
                ...(reportTypeUpdate ? { reportType: reportTypeUpdate } : {}),
              },
            },
          });

          if (pendingTemplateFile) {
            uploadResult = await performTemplateUpload(
              pendingTemplateFile,
              !shouldAutoApprove && !isSystemTemplate,
            );
          }
        }

        if ((isSystemTemplate || shouldAutoApprove) && uploadResult) {
          try {
            await approveVersion({
              variables: {
                templateId: uploadResult.templateId,
                versionId: uploadResult.versionId,
              },
            });
          } catch {
            toast.error(
              "Template was published successfully but auto-approve failed. Please approve the version manually.",
            );
          }
        }

        onPublishSuccess();
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    },
    [
      isUpdateMode,
      isNewTemplate,
      pendingTemplateFile,
      performVersionUpload,
      performTemplateUpload,
      templateIdNum,
      updateTemplate,
      generalSettings.templateName,
      generalSettings.templateType,
      originalSettings,
      isSystemTemplate,
      approveVersion,
      onPublishSuccess,
    ],
  );

  return {
    handlePublish,
    updateTemplate,
    isSaving,
    createLoading,
    completeLoading,
    updateLoading,
  };
};
